import { FC, useCallback, useState } from "react";
import { twMerge } from "tailwind-merge";

import { useAssessmentGradingFormContext } from "../../../../providers/AssessmentGradingFormProvider";
import { getStudentIdFromAccessId, isStudentAccessId } from "../../../../shared/access-id";
import { useFindItemBySearchParamId } from "../../../../utils/hooks/use-find-item-by-search-param-id";
import { isGroupPage } from "../../../../utils/page-utils";
import { useQueryParamsManager } from "../../../../utils/routing";
import { getUserFullName, getUserInitials } from "../../../../utils/user-utils";

import { ACCESS_ID_QUERY_PARAM, SUBMISSION_ID_QUERY_PARAM } from "components/constants";
import { NewSubmissionBadge } from "components/materials/page/assessment/NewSubmissionBadge";
import {
  getOfficialSubmission,
  getSubmissionsByAccessId,
  isSubmissionLate,
} from "components/materials/page/helpers";
import { ResponseType, useResponsesForPage } from "components/materials/page/responses";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { ClickEvent, Menu, MenuItem } from "mds/components/Menu";
import { PillButton, PillButtonProps } from "mds/components/PillButton";
import {
  ChevronDownIcon,
  LoadingDotsIcon,
  PersonGroupIcon,
  PersonSingleIcon,
  ReactionVoteYesIcon,
} from "mds/icons";
import { useAppSelector } from "store/index";
import {
  pageSelectors,
  sectionUserSelectors,
  selectCurrentSection,
  selectFullSubmissionById,
  selectSortedSubmissionsByPageId,
  userSelectors,
} from "store/selectors";
import { useListSelector } from "store/store-hooks";

type QueueDropdownOption = "all" | "assessed" | "unassessed" | "late";
const getQueueMenuDropdownOptions = (
  isGroup: boolean,
  allCount: number,
  assessedCount: number,
  unassessedCount: number,
  lateCount: number,
) => {
  const principalType = isGroup ? "group" : "student";

  return [
    {
      value: "all",
      label: t(`student_assessment_queue.${principalType}.all`, { num: allCount }),
    },
    {
      value: "assessed",
      label: t(`student_assessment_queue.${principalType}.assessed`, {
        num: assessedCount,
      }),
    },
    {
      value: "unassessed",
      label: t(`student_assessment_queue.${principalType}.unassessed`, {
        num: unassessedCount,
      }),
    },
    { value: "late", label: t("student_assessment_queue.late", { num: lateCount }) },
  ] as { value: QueueDropdownOption; label: string }[];
};

interface AssessmentStudentQueueDropdownMenuProps {
  selectedOption: QueueDropdownOption;
  onOptionChange: (option: QueueDropdownOption) => void;
  isGroup: boolean;
  allCount: number;
  assessedCount: number;
  unassessedCount: number;
  lateCount: number;
}
const AssessmentStudentQueueDropdownMenu: FC<AssessmentStudentQueueDropdownMenuProps> = ({
  selectedOption,
  onOptionChange,
  isGroup,
  allCount,
  assessedCount,
  unassessedCount,
  lateCount,
}) => {
  const options = getQueueMenuDropdownOptions(
    isGroup,
    allCount,
    assessedCount,
    unassessedCount,
    lateCount,
  );

  const selectedOptionData = options.find((opt) => opt.value === selectedOption);

  const onItemClick = (e: ClickEvent) => {
    const newOption = e.value as QueueDropdownOption;
    onOptionChange(newOption);
  };

  const menuButton = (
    <Button
      className="flex w-[206px] items-center justify-between gap-1 py-2 text-black-tint-20"
      kind="white"
      size="s"
    >
      {selectedOptionData?.label}
      <ChevronDownIcon />
    </Button>
  );

  return (
    <div className="mb-4 flex">
      <Menu direction="bottom" menuButton={menuButton} position="initial">
        {options.map((option) => {
          const selected = selectedOption === option.value;
          const isDisabled =
            (option.value === "late" && lateCount === 0) ||
            (option.value === "assessed" && assessedCount === 0) ||
            (option.value === "unassessed" && unassessedCount === 0) ||
            (option.value === "all" && allCount === 0);

          return (
            <MenuItem
              checked={selected}
              disabled={isDisabled}
              eventProps={{ fieldValue: option.value }}
              key={option.value}
              value={option.value}
              onClick={onItemClick}
            >
              {option.label}
            </MenuItem>
          );
        })}
      </Menu>
    </div>
  );
};

const StudentUserAvatar = ({
  userId,
  isSelected,
  anonymize,
}: {
  userId: string;
  isSelected?: boolean;
  anonymize?: boolean;
}) => {
  const user = useAppSelector((s) => userSelectors.selectById(s, userId));

  return (
    <div
      className={twMerge(
        "flex size-3 items-center justify-center rounded-full p-3 text-[9px]",
        isSelected ? "text-white" : "text-blue",
        !isSelected && "bg-blue-tint-93",
      )}
    >
      {anonymize ? <PersonSingleIcon size="xs" /> : getUserInitials(user)}
    </div>
  );
};

const GroupAvatar = ({ isSelected }: { isSelected?: boolean }) => {
  return (
    <div
      className={twMerge(
        "flex size-3 items-center justify-center rounded-full p-3 text-[9px]",
        !isSelected && "bg-hibiscus-tint-95",
      )}
    >
      <PersonGroupIcon className={isSelected ? "text-white" : "text-hibiscus"} />
    </div>
  );
};

const AssessmentItemIcon = ({
  accessId,
  isSelected,
  isAssessedResponse,
  anonymize,
}: {
  accessId: string;
  isSelected?: boolean;
  isAssessedResponse?: boolean;
  anonymize?: boolean;
}) => {
  if (isAssessedResponse) {
    return (
      <div className="flex min-h-6 min-w-6 items-center justify-center">
        <ReactionVoteYesIcon />
      </div>
    );
  }

  if (isStudentAccessId(accessId)) {
    return (
      <StudentUserAvatar
        anonymize={anonymize}
        isSelected={isSelected}
        userId={getStudentIdFromAccessId(accessId)}
      />
    );
  }

  return <GroupAvatar isSelected={isSelected} />;
};

interface AssessmentStudentQueueProps {
  pageId: string;
}

export const AssessmentStudentQueue: FC<AssessmentStudentQueueProps> = ({ pageId }) => {
  const { isAiAssessmentLoading } = useAssessmentGradingFormContext();
  const currentSection = useAppSelector(selectCurrentSection);
  const { setQueryParams, getQueryParam } = useQueryParamsManager();
  const page = useAppSelector((s) => pageSelectors.selectById(s, pageId));
  const sectionUsers = useListSelector(sectionUserSelectors.filterForCurrentSection);
  const users = useAppSelector(userSelectors.selectEntities);
  const submissions = useAppSelector((s) => selectSortedSubmissionsByPageId(s, pageId));
  const { responses } = useResponsesForPage(page);

  const submissionId = getQueryParam(SUBMISSION_ID_QUERY_PARAM);
  const currentSubmission = useAppSelector((s) => selectFullSubmissionById(s, submissionId));

  const [filterBy, setFilterBy] = useState<QueueDropdownOption>("all");

  const assessmentsPublished = !!page?.assessments_published_at;
  const isGroup = !!page && isGroupPage(page);

  const anonymize = !assessmentsPublished && currentSection?.anonymous_grading_enabled;
  const selectedResponse = useFindItemBySearchParamId(responses, ACCESS_ID_QUERY_PARAM, true);

  const assessedResponseById = Object.fromEntries(
    responses
      .filter((response) => {
        const studentSubmissions = getSubmissionsByAccessId(response.id, sectionUsers, submissions);
        return !!getOfficialSubmission(studentSubmissions);
      })
      .map((response) => [response.id, response]),
  );
  const assessedResponses = Object.values(assessedResponseById);

  const unassessedResponses = responses.filter((response) => !assessedResponseById[response.id]);

  const lateResponses = responses.filter((response) => {
    const studentSubmissions = getSubmissionsByAccessId(response.id, sectionUsers, submissions);
    return studentSubmissions.some((submission) =>
      page?.due_at ? isSubmissionLate(submission, page?.due_at) : false,
    );
  });

  const filteredResponses =
    filterBy === "late"
      ? lateResponses
      : filterBy === "assessed"
        ? assessedResponses
        : filterBy === "unassessed"
          ? unassessedResponses
          : responses;

  const handleResponseClick = useCallback(
    (response: ResponseType) => {
      setQueryParams(
        {
          [ACCESS_ID_QUERY_PARAM]: response.id,
          [SUBMISSION_ID_QUERY_PARAM]: null,
        },
        { replace: false },
      );
    },
    [setQueryParams],
  );

  return (
    <div className="relative">
      {isAiAssessmentLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50">
          <LoadingDotsIcon />
        </div>
      )}

      <div
        className="ml-auto h-full w-[240px] overflow-y-auto rounded-2xl border border-black-tint-90 bg-white p-4"
        role="list"
      >
        <AssessmentStudentQueueDropdownMenu
          allCount={responses.length}
          assessedCount={assessedResponses.length}
          isGroup={isGroup}
          lateCount={lateResponses.length}
          selectedOption={filterBy}
          unassessedCount={unassessedResponses.length}
          onOptionChange={setFilterBy}
        />
        {filteredResponses.map((response) => {
          const isSelected = selectedResponse?.id === response.id;
          let title = response.title;
          if (isStudentAccessId(response.id) && !anonymize) {
            const user = users[getStudentIdFromAccessId(response.id)];
            if (user) {
              title = getUserFullName(user);
            }
          }

          const studentSubmissions = getSubmissionsByAccessId(
            response.id,
            sectionUsers,
            submissions,
          );

          const submitBtnAttrs: PillButtonProps = currentSubmission
            ? {
                form: "assessment-grading-form",
                type: "submit",
                name: "submitSource",
                value: `queue:${response.id}`,
              }
            : { onClick: () => handleResponseClick(response) };

          return (
            <PillButton
              aria-label={title}
              aria-selected={isSelected || undefined}
              className={twMerge(
                "my-1 flex h-8 w-full min-w-12 items-center justify-start gap-2 rounded-lg px-2 hover:bg-black-tint-97 data-[selected]:border-blue-tint-40 data-[selected]:bg-blue data-[selected]:text-white",
                isSelected ? "text-white" : "text-black-tint-20",
              )}
              data-selected={isSelected || undefined}
              eventName="Assessment - Student Queue"
              eventProps={{
                pageId: page?.id,
                userId: response.id,
                submissionId: currentSubmission?.id,
              }}
              key={response.id}
              role="option"
              size="s"
              tabIndex={isSelected ? 0 : -1}
              title={title}
              {...submitBtnAttrs}
            >
              <AssessmentItemIcon
                accessId={response.id}
                anonymize={anonymize}
                isAssessedResponse={!!assessedResponseById[response.id]}
                isSelected={isSelected}
              />
              <div
                className={twMerge(
                  "min-w-0 truncate text-sm font-medium",
                  isSelected ? "text-white" : "text-black-tint-20",
                )}
              >
                {title}
              </div>
              <NewSubmissionBadge sortedSubmissions={studentSubmissions} />
            </PillButton>
          );
        })}
      </div>
    </div>
  );
};
